import { formatFCFA } from "./fcfa";

const styles = `
  <style>
    @page { size: A4; margin: 1.5cm; }
    body { font-family: 'IBM Plex Sans', Arial, sans-serif; color: #121212; margin: 0; }
    h1, h2, h3 { font-family: 'Cabinet Grotesk', Arial, sans-serif; }
    .header { display: flex; align-items: center; gap: 16px; border-bottom: 3px solid #C62828; padding-bottom: 12px; margin-bottom: 24px; }
    .header .logo { width: 64px; height: 64px; background: #C62828; color: white; border-radius: 4px; display:flex; align-items:center; justify-content:center; font-size: 22px; font-weight: 800; }
    .header .meta { flex: 1; }
    .header h1 { margin: 0; font-size: 24px; }
    .header p { margin: 2px 0 0; color: #616161; font-size: 12px; }
    .overline { text-transform: uppercase; letter-spacing: .16em; font-size: 10px; color: #C62828; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; }
    th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #E0E0E0; }
    th { background: #F4F5F7; text-transform: uppercase; letter-spacing: .12em; font-size: 10px; color: #616161; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .footer { margin-top: 36px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; font-size: 11px; color: #616161; }
    .sig-box { border-top: 1px solid #999; padding-top: 6px; min-height: 60px; }
    .confidential { margin-top: 24px; font-size: 10px; color: #999; text-align: center; }
    .watermark { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0.06; pointer-events: none; z-index: -1; font-size: 120px; font-weight: 900; transform: rotate(-30deg); }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; }
    .badge-success { background: #2E7D32; color: white; }
    .badge-error { background: #C62828; color: white; }
    .card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0; }
    .stat { background: #F4F5F7; padding: 10px 14px; border-radius: 4px; }
    .stat .v { font-size: 18px; font-weight: 700; font-family: 'Cabinet Grotesk', sans-serif; }
    .stat .l { font-size: 10px; color: #616161; text-transform: uppercase; letter-spacing: .12em; }
  </style>
`;

function header(club) {
  return `
    <div class="header">
      <div class="logo">${(club?.name || "AS").slice(0, 2).toUpperCase()}</div>
      <div class="meta">
        <h1>${club?.name || "Club Sportif"}</h1>
        <p>${club?.subtitle || ""} · ${club?.city || ""}${club?.country ? ", " + club.country : ""}</p>
      </div>
      <div style="text-align:right;font-size:11px;color:#616161;">
        ${new Date().toLocaleDateString("fr-FR", { dateStyle: "long" })}
      </div>
    </div>
  `;
}

function watermark(club) {
  if (club?.show_watermark === false) return "";
  return `<div class="watermark">${(club?.name || "ASC").toUpperCase()}</div>`;
}

function signatureBoxes(club, leftLabel = "Président", rightLabel = "Trésorier") {
  return `
    <div class="footer">
      <div class="sig-box">
        <div class="overline">${leftLabel}</div>
      </div>
      <div class="sig-box">
        <div class="overline">${rightLabel}</div>
      </div>
    </div>
    <p class="confidential">Document confidentiel — ${club?.name || "Club"} · Émis le ${new Date().toLocaleDateString("fr-FR")}</p>
  `;
}

function printHTML(title, body) {
  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${title}</title>${styles}</head><body>${body}<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) {
    alert("Veuillez autoriser les pop-ups pour imprimer.");
    return;
  }
  w.document.write(html);
  w.document.close();
}

export function printPayslip(club, employee, payslip) {
  const body = `
    ${watermark(club)}
    ${header(club)}
    <p class="overline">Bulletin de paie</p>
    <h2>${new Date(payslip.year, payslip.month - 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h2>
    <div class="card-grid">
      <div class="stat"><div class="l">Employé</div><div class="v">${employee.first_name} ${employee.last_name}</div></div>
      <div class="stat"><div class="l">Poste</div><div class="v" style="font-size:14px">${employee.position}</div></div>
      <div class="stat"><div class="l">Département</div><div class="v" style="font-size:14px">${employee.department || "—"}</div></div>
      <div class="stat"><div class="l">N° CNSS</div><div class="v" style="font-size:14px">${employee.cnss_number || "—"}</div></div>
    </div>
    <table>
      <thead><tr><th>Rubrique</th><th class="num">Montant (FCFA)</th></tr></thead>
      <tbody>
        <tr><td>Salaire de base</td><td class="num">${formatFCFA(payslip.base_salary)}</td></tr>
        <tr><td>Primes / Bonus</td><td class="num">+ ${formatFCFA(payslip.bonuses)}</td></tr>
        <tr><td>Retenues</td><td class="num">− ${formatFCFA(payslip.deductions)}</td></tr>
        <tr><td>CNSS salarié</td><td class="num">− ${formatFCFA(payslip.cnss_employee)}</td></tr>
        <tr><td>IRPP</td><td class="num">− ${formatFCFA(payslip.irpp)}</td></tr>
        <tr style="background:#F4F5F7;font-weight:700;"><td>NET À PAYER</td><td class="num" style="color:#C62828">${formatFCFA(payslip.net_salary)}</td></tr>
        <tr><td colspan="2" style="font-size:10px;color:#616161;">Cotisation patronale CNSS (à la charge du club) : ${formatFCFA(payslip.cnss_employer)}</td></tr>
      </tbody>
    </table>
    ${signatureBoxes(club, "Employeur", "Salarié")}
  `;
  printHTML(`Bulletin ${employee.last_name} ${payslip.year}-${payslip.month}`, body);
}

export function printMemberCard(club, member) {
  const photoSrc = member.photo_url ? `<img src="${member.photo_url.startsWith('http') ? member.photo_url : process.env.REACT_APP_BACKEND_URL + member.photo_url + '?auth=' + localStorage.getItem('asc_token')}" style="width:80px;height:80px;border-radius:4px;object-fit:cover;" />` : `<div style="width:80px;height:80px;border-radius:4px;background:#C62828;color:white;display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:800;">${(member.first_name?.[0] || "")}${(member.last_name?.[0] || "")}</div>`;
  const body = `
    <div style="max-width: 360px; margin: 40px auto; border: 2px solid #C62828; border-radius: 8px; overflow: hidden; background: white; font-family: Arial, sans-serif;">
      <div style="background: linear-gradient(135deg, #9E1B1B, #C62828); color: white; padding: 14px 18px;">
        <div style="font-size: 9px; letter-spacing: .18em; opacity: .8;">CARTE DE MEMBRE</div>
        <div style="font-size: 16px; font-weight: 800; font-family: 'Cabinet Grotesk', sans-serif;">${club?.name || "Club Sportif"}</div>
      </div>
      <div style="padding: 18px; display: flex; gap: 14px; align-items: center;">
        ${photoSrc}
        <div>
          <div style="font-size: 16px; font-weight: 700;">${member.first_name} ${member.last_name}</div>
          <div style="font-size: 11px; color: #616161; text-transform: uppercase; letter-spacing: .12em; margin-top: 4px;">${member.type}</div>
          <div style="font-size: 11px; color: #C62828; margin-top: 6px; font-weight: 600;">${member.member_number}</div>
        </div>
      </div>
      <div style="padding: 0 18px 14px; font-size: 11px; color: #616161;">
        <div>Membre depuis : ${new Date(member.member_since).toLocaleDateString("fr-FR")}</div>
        ${member.expires_at ? `<div>Valide jusqu'au : ${new Date(member.expires_at).toLocaleDateString("fr-FR")}</div>` : ""}
      </div>
      <div style="background: #FBC02D; padding: 6px 18px; font-size: 10px; color: #121212; letter-spacing: .12em; text-align: center; font-weight: 600;">CETTE CARTE EST PERSONNELLE ET NON CESSIBLE</div>
    </div>
  `;
  printHTML(`Carte membre ${member.member_number}`, body);
}

export function printAttestation(club, employee) {
  const body = `
    ${watermark(club)}
    ${header(club)}
    <p class="overline">Attestation</p>
    <h2>Attestation de travail</h2>
    <p style="line-height: 1.8; margin-top: 20px;">
      Je soussigné(e), <strong>${club?.name}</strong>, atteste par la présente que <strong>${employee.first_name} ${employee.last_name}</strong>
      est employé(e) au sein de notre structure en qualité de <strong>${employee.position}</strong>
      ${employee.department ? `dans le département <strong>${employee.department}</strong>` : ""}, 
      sous contrat ${employee.contract_type}${employee.contract_start ? ` depuis le ${new Date(employee.contract_start).toLocaleDateString("fr-FR")}` : ""}.
    </p>
    <p style="line-height: 1.8;">
      Le présent document est délivré à l'intéressé(e) pour servir et valoir ce que de droit.
    </p>
    <p style="margin-top: 30px;">Fait à ${club?.city || "—"}, le ${new Date().toLocaleDateString("fr-FR", { dateStyle: "long" })}.</p>
    ${signatureBoxes(club, "Le Président", "Le Directeur Général")}
  `;
  printHTML(`Attestation ${employee.last_name}`, body);
}

export function printFinanceReport(club, summary, transactions, period) {
  const body = `
    ${watermark(club)}
    ${header(club)}
    <p class="overline">Rapport financier</p>
    <h2>Rapport ${period}</h2>
    <div class="card-grid">
      <div class="stat"><div class="l">Recettes</div><div class="v" style="color:#2E7D32">${formatFCFA(summary.total_recette)}</div></div>
      <div class="stat"><div class="l">Dépenses</div><div class="v" style="color:#C62828">${formatFCFA(summary.total_depense)}</div></div>
      <div class="stat"><div class="l">Solde</div><div class="v" style="color:${summary.solde >= 0 ? '#2E7D32' : '#C62828'}">${formatFCFA(summary.solde)}</div></div>
      <div class="stat"><div class="l">Transactions</div><div class="v">${transactions.length}</div></div>
    </div>
    <h3 style="margin-top:24px;">Détail des transactions</h3>
    <table>
      <thead><tr><th>Date</th><th>Description</th><th>Catégorie</th><th>Type</th><th class="num">Montant</th></tr></thead>
      <tbody>
        ${transactions.map((t) => `
          <tr><td>${t.transaction_date}</td><td>${t.description}</td><td>${t.category_name || "—"}</td>
            <td><span class="badge ${t.type === 'recette' ? 'badge-success' : 'badge-error'}">${t.type}</span></td>
            <td class="num">${t.type === "recette" ? "+" : "−"} ${formatFCFA(t.amount)}</td></tr>
        `).join("")}
      </tbody>
    </table>
    ${signatureBoxes(club, "Trésorier", "Président")}
  `;
  printHTML(`Rapport financier ${period}`, body);
}

export function printPlayerCard(club, player, stats) {
  const photoSrc = player.photo_url ? `<img src="${player.photo_url.startsWith('http') ? player.photo_url : process.env.REACT_APP_BACKEND_URL + player.photo_url + '?auth=' + localStorage.getItem('asc_token')}" style="width:140px;height:140px;border-radius:4px;object-fit:cover;border:2px solid #C62828;" />` : `<div style="width:140px;height:140px;border-radius:4px;background:#C62828;color:white;display:flex;align-items:center;justify-content:center;font-size:42px;font-weight:800;">${(player.first_name?.[0] || "")}${(player.last_name?.[0] || "")}</div>`;
  const body = `
    ${watermark(club)}
    ${header(club)}
    <p class="overline">Fiche joueur</p>
    <h2>${player.first_name} ${player.last_name}</h2>
    <div style="display:flex; gap: 20px; margin-top: 16px;">
      ${photoSrc}
      <div style="flex:1;">
        <div class="card-grid" style="margin:0;">
          <div class="stat"><div class="l">Poste</div><div class="v" style="font-size:14px">${player.position || "—"}</div></div>
          <div class="stat"><div class="l">N° maillot</div><div class="v">${player.jersey_number ?? "—"}</div></div>
          <div class="stat"><div class="l">Statut</div><div class="v" style="font-size:14px">${player.status}</div></div>
          <div class="stat"><div class="l">Nationalité</div><div class="v" style="font-size:14px">${player.nationality || "—"}</div></div>
          <div class="stat"><div class="l">Né le</div><div class="v" style="font-size:14px">${player.date_of_birth || "—"}</div></div>
          <div class="stat"><div class="l">Équipe</div><div class="v" style="font-size:14px">${player.team_name || "—"}</div></div>
        </div>
      </div>
    </div>
    ${stats ? `
    <h3 style="margin-top:24px;">Statistiques saison</h3>
    <div class="card-grid">
      <div class="stat"><div class="l">Matchs</div><div class="v">${stats.matches}</div></div>
      <div class="stat"><div class="l">Buts</div><div class="v">${stats.goals}</div></div>
      <div class="stat"><div class="l">Passes décisives</div><div class="v">${stats.assists}</div></div>
      <div class="stat"><div class="l">Minutes jouées</div><div class="v">${stats.minutes}</div></div>
      <div class="stat"><div class="l">Cartons jaunes</div><div class="v">${stats.yellow_cards}</div></div>
      <div class="stat"><div class="l">Cartons rouges</div><div class="v">${stats.red_cards}</div></div>
    </div>` : ""}
    ${signatureBoxes(club, "Le Coach", "Le Président")}
  `;
  printHTML(`Fiche ${player.last_name}`, body);
}


export function printMatchSheet(club, sheet, players, employees) {
  const playerMap = Object.fromEntries((players || []).map((p) => [p.id, p]));
  const empMap = Object.fromEntries((employees || []).map((e) => [e.id, e]));
  const lineup = sheet.lineup || [];
  const titulaires = lineup.filter((l) => l.role === "titulaire");
  const remplacants = lineup.filter((l) => l.role === "remplacant");
  const fmtPlayer = (l) => {
    const p = playerMap[l.player_id];
    if (!p) return `<em>Joueur supprimé</em>`;
    return `<strong>${l.jersey_number ?? "—"}</strong> · ${p.last_name.toUpperCase()} ${p.first_name}${l.position ? ` <span style="color:#616161">(${l.position})</span>` : ""}`;
  };
  const fmtStaff = (id) => {
    const e = empMap[id];
    return e ? `${e.first_name} ${e.last_name}` : "—";
  };

  const rowsTit = titulaires.length
    ? titulaires.map((l) => `<tr><td>${fmtPlayer(l)}</td></tr>`).join("")
    : `<tr><td style="color:#999;font-style:italic;">Aucun titulaire</td></tr>`;
  const rowsRem = remplacants.length
    ? remplacants.map((l) => `<tr><td>${fmtPlayer(l)}</td></tr>`).join("")
    : `<tr><td style="color:#999;font-style:italic;">Aucun remplaçant</td></tr>`;

  const evTypeLabel = {
    but: "⚽ But", passe_decisive: "🅰 Passe D.", carton_jaune: "🟨 Jaune",
    carton_rouge: "🟥 Rouge", remplacement: "🔁 Remplacement", blessure: "🩹 Blessure",
  };
  const events = (sheet.events || []).slice().sort((a, b) => (a.minute || 0) - (b.minute || 0));
  const eventsRows = events.length
    ? events.map((e) => {
      const p = playerMap[e.player_id];
      const pName = p ? `${p.last_name} ${p.first_name}` : "—";
      const pIn = e.player_in_id ? playerMap[e.player_in_id] : null;
      const extra = pIn ? ` ⇄ ${pIn.last_name} ${pIn.first_name}` : "";
      return `<tr><td class="num">${e.minute ?? "—"}'</td><td>${evTypeLabel[e.type] || e.type}</td><td>${pName}${extra}</td><td>${e.note || ""}</td></tr>`;
    }).join("")
    : `<tr><td colspan="4" style="color:#999;font-style:italic;text-align:center;">Aucun événement enregistré</td></tr>`;

  const statusBadge = {
    brouillon: '<span class="badge" style="background:#FBC02D;color:#121212;">Brouillon</span>',
    validee: '<span class="badge" style="background:#1976D2;color:white;">Validée</span>',
    jouee: '<span class="badge badge-success">Jouée</span>',
  };

  const showScore = sheet.score_home !== null && sheet.score_home !== undefined;
  const homeTeam = sheet.home_away === "domicile" ? (club?.name || "—") : sheet.opponent;
  const awayTeam = sheet.home_away === "domicile" ? sheet.opponent : (club?.name || "—");

  const body = `
    ${watermark(club)}
    ${header(club)}
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
      <div>
        <div class="overline">Feuille de match</div>
        <h2 style="margin:4px 0 0;font-size:22px;">${sheet.competition || "Match"} — ${sheet.home_away === "domicile" ? "à domicile" : "à l'extérieur"}</h2>
      </div>
      <div style="text-align:right;">
        ${statusBadge[sheet.status] || ""}
      </div>
    </div>

    <div style="background:#F4F5F7;padding:14px;border-radius:6px;margin-bottom:18px;display:grid;grid-template-columns:repeat(4,1fr);gap:10px;font-size:11px;">
      <div><div class="overline">Date</div><div style="font-weight:700;font-size:13px;margin-top:2px;">${new Date(sheet.date).toLocaleDateString("fr-FR", { dateStyle: "long" })}${sheet.time ? ` · ${sheet.time}` : ""}</div></div>
      <div><div class="overline">Lieu</div><div style="font-weight:700;font-size:13px;margin-top:2px;">${sheet.location || "—"}</div></div>
      <div><div class="overline">Arbitre</div><div style="font-weight:700;font-size:13px;margin-top:2px;">${sheet.referee || "—"}</div></div>
      <div><div class="overline">Formation</div><div style="font-weight:700;font-size:13px;margin-top:2px;">${sheet.formation || "—"}</div></div>
    </div>

    ${showScore ? `
    <div style="display:flex;align-items:center;justify-content:center;gap:24px;padding:20px;background:#0A4628;color:white;border-radius:6px;margin-bottom:18px;">
      <div style="text-align:right;flex:1;"><div style="font-size:13px;opacity:.8;">${homeTeam}</div></div>
      <div style="font-family:'Cabinet Grotesk',sans-serif;font-size:36px;font-weight:900;">${sheet.score_home} - ${sheet.score_away}</div>
      <div style="text-align:left;flex:1;"><div style="font-size:13px;opacity:.8;">${awayTeam}</div></div>
    </div>` : ""}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px;">
      <div>
        <div class="overline">Titulaires (${titulaires.length}/11)</div>
        <table style="margin-top:6px;">${rowsTit}</table>
      </div>
      <div>
        <div class="overline">Remplaçants (${remplacants.length})</div>
        <table style="margin-top:6px;">${rowsRem}</table>
      </div>
    </div>

    <div style="margin-top:20px;">
      <div class="overline">Encadrement</div>
      <table style="margin-top:6px;">
        <tr><th style="width:30%;">Entraîneur principal</th><td>${fmtStaff(sheet.coach_id)}</td></tr>
        <tr><th>Entraîneur adjoint</th><td>${fmtStaff(sheet.assistant_id)}</td></tr>
        <tr><th>Médecin</th><td>${fmtStaff(sheet.doctor_id)}</td></tr>
        <tr><th>Capitaine</th><td>${sheet.captain_id ? (playerMap[sheet.captain_id] ? `${playerMap[sheet.captain_id].last_name} ${playerMap[sheet.captain_id].first_name}` : "—") : "—"}</td></tr>
      </table>
    </div>

    ${events.length || sheet.status === "jouee" ? `
    <div style="margin-top:20px;">
      <div class="overline">Événements du match</div>
      <table style="margin-top:6px;">
        <thead><tr><th class="num">Min.</th><th>Type</th><th>Joueur</th><th>Note</th></tr></thead>
        <tbody>${eventsRows}</tbody>
      </table>
    </div>` : ""}

    ${sheet.notes ? `<div style="margin-top:18px;padding:12px;background:#FBC02D14;border-left:3px solid #FBC02D;font-size:11px;"><strong>Notes :</strong> ${sheet.notes}</div>` : ""}

    <div class="footer" style="margin-top:32px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;font-size:11px;color:#616161;">
      <div class="sig-box"><div class="overline">Entraîneur</div></div>
      <div class="sig-box"><div class="overline">Capitaine</div></div>
      <div class="sig-box"><div class="overline">Arbitre</div></div>
    </div>
    <p class="confidential">Document confidentiel — ${club?.name || "Club"} · Émis le ${new Date().toLocaleDateString("fr-FR")}</p>
  `;
  printHTML(`Feuille de match ${sheet.opponent} ${sheet.date}`, body);
}
